// Canonical frequency is an annualized float (0.2 = 1-in-5-year); the scenario form stores a
// times/years Int pair. These convert losslessly and deliberately do NOT clamp the percentage,
// so values >= 1 render as > 100% (unlike FrequencyIndicator).

/** Sentinel chosenSourceId for the triangulated suggestion. FE-only; dropped at the wire. */
export const TRIANGULATED = 'triangulated';

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

export interface TimesYears {
  times: number;
  years: number;
}

/** Express an annualized rate as a GCD-reduced { times, years } pair (no >= 1 special-case). */
export const annualizedToTimesYears = (value: number): TimesYears => {
  if (!isFinite(value) || value <= 0) return { times: 0, years: 1 };
  let times = Math.round(value * 100);
  const years = 100;
  if (times === 0) times = 1; // guard sub-0.5% rates from collapsing to "0 events"
  const divisor = gcd(times, years) || 1;
  return { times: times / divisor, years: years / divisor };
};

export const timesYearsToAnnualized = (times: number, years: number): number =>
  years === 0 ? 0 : times / years;

export const annualChancePercent = (value: number): number => Math.round(value * 100);

export const formatFrequency = (value: number): string => {
  const { times, years } = annualizedToTimesYears(value);
  const eventWord = times === 1 ? 'event' : 'events';
  const yearWord = years === 1 ? 'year' : 'years';
  return `${times} ${eventWord} in ${years} ${yearWord} (${annualChancePercent(value)}% annual chance)`;
};

/** Display-only: smallest sensible denominator approximating the value, paired with the exact %. */
export const niceFrequencyDisplay = (value: number): string => {
  if (!isFinite(value) || value <= 0) return formatFrequency(value);

  let best = { times: 0, years: 0, err: Infinity };
  for (let years = 1; years <= 12; years += 1) {
    const times = Math.round(value * years);
    if (times <= 0) continue;
    const err = Math.abs(times / years - value);
    if (err < best.err) best = { times, years, err };
    if (err <= 0.02) break; // close enough — prefer the smallest denominator
  }

  // No fit within 12 years (a sub-~4% rate) — the /100 form (which guards times→1) beats "0 events".
  if (best.err === Infinity) return formatFrequency(value);

  const eventWord = best.times === 1 ? 'event' : 'events';
  const yearWord = best.years === 1 ? 'year' : 'years';
  return `${best.times} ${eventWord} in ${best.years} ${yearWord} (${annualChancePercent(value)}% annual chance)`;
};
